import React, { useEffect, useState, useCallback, useRef } from 'react';
import axiosInstance from '../../Services/axiosInstance';
import LoadingSpinner from '../common/LoadingSpinner';
import { useToaster } from '../common/Toaster';
import { getApiErrorDetails } from '../../types/api';
import { ApplicationLogDto, PagedLogsResponse } from '../../types/logs';
import {
  Card,
  CardContent,
} from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../ui/pagination';
import { X, Search, RefreshCw, AlertCircle, AlertTriangle, Info, FileText, Trash2, Copy } from 'lucide-react';

const levelIcon = (level: string) => {
  switch (level) {
    case 'Error':
      return <AlertCircle size={14} className="text-destructive" />;
    case 'Warning':
      return <AlertTriangle size={14} className="text-yellow-500" />;
    default:
      return <Info size={14} className="text-blue-500" />;
  }
};

const levelClass = (level: string) => {
  switch (level) {
    case 'Error':
      return 'text-destructive font-medium';
    case 'Warning':
      return 'text-yellow-600 dark:text-yellow-400 font-medium';
    default:
      return 'text-blue-600 dark:text-blue-400';
  }
};

const formatTimestamp = (ts: string) => {
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const LogsTable: React.FC = () => {
  const { notify } = useToaster();
  const [data, setData] = useState<ApplicationLogDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const [autoRefresh, setAutoRefresh] = useState(false);
  const autoRefreshRef = useRef(autoRefresh);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [selectedLog, setSelectedLog] = useState<ApplicationLogDto | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Keep ref in sync
  useEffect(() => {
    autoRefreshRef.current = autoRefresh;
  }, [autoRefresh]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedQuery, levelFilter]);

  const fetchLogs = useCallback(async () => {
    try {
      const params: Record<string, string | number> = {
        page: currentPage,
        pageSize: itemsPerPage,
      };
      if (levelFilter && levelFilter !== 'all') params.level = levelFilter;
      if (debouncedQuery) params.search = debouncedQuery;

      const response = await axiosInstance.get<PagedLogsResponse>('/api/Logs', { params });
      setData(response.data.items);
      setTotalCount(response.data.totalCount);
      setTotalPages(response.data.totalPages);
    } catch (error: unknown) {
      const errorDetails = getApiErrorDetails(error);
      notify('Error', errorDetails.message, 'danger');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, levelFilter, debouncedQuery, notify]);

  // Initial fetch + refetch on param changes
  useEffect(() => {
    setLoading(true);
    fetchLogs();
  }, [fetchLogs]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        fetchLogs();
      }, 10000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, fetchLogs]);

  const handleClearSearch = useCallback(() => {
    setQuery('');
    setDebouncedQuery('');
  }, []);

  const handleClearLogs = useCallback(async () => {
    if (!window.confirm('Are you sure you want to clear all logs? This cannot be undone.')) return;
    setClearing(true);
    try {
      await axiosInstance.delete('/api/Logs');
      notify('Success', 'All logs cleared', 'success');
      setData([]);
      setTotalCount(0);
      setTotalPages(0);
      setCurrentPage(1);
    } catch (error: unknown) {
      const errorDetails = getApiErrorDetails(error);
      notify('Error', errorDetails.message, 'danger');
    } finally {
      setClearing(false);
    }
  }, [notify]);

  const handleExportLogs = useCallback(async () => {
    setExporting(true);
    try {
      const params: Record<string, string | number> = { maxEntries: 500 };
      if (levelFilter && levelFilter !== 'all') params.level = levelFilter;
      if (debouncedQuery) params.search = debouncedQuery;

      const response = await axiosInstance.get<string>('/api/Logs/export', { params });
      await navigator.clipboard.writeText(response.data);
      notify('Copied', 'Logs copied to clipboard — paste into Claude', 'success');
    } catch (error: unknown) {
      const errorDetails = getApiErrorDetails(error);
      notify('Error', errorDetails.message, 'danger');
    } finally {
      setExporting(false);
    }
  }, [levelFilter, debouncedQuery, notify]);

  const handleRowClick = (log: ApplicationLogDto) => {
    setSelectedLog(log);
    setIsDetailOpen(true);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    const pageNumbers: (number | string)[] = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      pageNumbers.push(1);
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 3) {
        start = 2;
        end = Math.min(totalPages - 1, maxPagesToShow - 1);
      }
      if (currentPage >= totalPages - 2) {
        start = Math.max(2, totalPages - (maxPagesToShow - 2));
        end = totalPages - 1;
      }
      if (start > 2) pageNumbers.push('ellipsis1');
      for (let i = start; i <= end; i++) pageNumbers.push(i);
      if (end < totalPages - 1) pageNumbers.push('ellipsis2');
      pageNumbers.push(totalPages);
    }
    return pageNumbers;
  };

  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);

  if (loading && data.length === 0) {
    return <LoadingSpinner label="Loading logs..." />;
  }

  return (
    <div className="w-full max-w-7xl space-y-4">
      {/* Toolbar */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Label htmlFor="level-filter" className="text-sm font-medium">Level</Label>
                <Select value={levelFilter} onValueChange={setLevelFilter}>
                  <SelectTrigger id="level-filter" className="w-[130px]">
                    <SelectValue placeholder="All levels" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All levels</SelectItem>
                    <SelectItem value="Error">Error</SelectItem>
                    <SelectItem value="Warning">Warning</SelectItem>
                    <SelectItem value="Information">Information</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {levelFilter !== 'all' && (
                <Badge variant={
                  levelFilter === 'Error' ? 'destructive' :
                  levelFilter === 'Warning' ? 'warning' : 'default'
                }>
                  {levelFilter}
                </Badge>
              )}

              <div className="flex items-center gap-2">
                <Switch
                  id="auto-refresh"
                  checked={autoRefresh}
                  onCheckedChange={setAutoRefresh}
                />
                <Label htmlFor="auto-refresh" className="text-sm cursor-pointer">
                  Auto-refresh
                </Label>
              </div>

              <Button variant="outline" size="sm" onClick={fetchLogs} title="Refresh now">
                <RefreshCw size={14} />
              </Button>

              <Button variant="outline" size="sm" onClick={handleExportLogs} disabled={exporting} title="Copy logs to clipboard for AI context">
                <Copy size={14} className="mr-1" />
                {exporting ? 'Copying...' : 'Export for AI'}
              </Button>

              <Button variant="destructive" size="sm" onClick={handleClearLogs} disabled={clearing} title="Clear all logs">
                <Trash2 size={14} className="mr-1" />
                {clearing ? 'Clearing...' : 'Clear Logs'}
              </Button>
            </div>

            <div className="flex items-center space-x-2 w-full max-w-sm">
              <div className="relative w-full">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="log-search"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Message, source, path..."
                  className="w-full pl-8 pr-8"
                  aria-label="Search logs"
                />
                {query && (
                  <button
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    onClick={handleClearSearch}
                    aria-label="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="text-sm text-muted-foreground">
        Showing {startItem}-{endItem} of {totalCount} log entries
        {debouncedQuery ? ` matching "${debouncedQuery}"` : ''}
        {levelFilter !== 'all' ? ` (${levelFilter})` : ''}
      </div>

      {data.length === 0 ? (
        <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg py-12">
          <div className="flex flex-col items-center gap-2 text-center">
            <div className="rounded-full bg-muted p-3">
              <FileText size={24} className="text-muted-foreground" />
            </div>
            <p className="font-medium text-muted-foreground">No logs found</p>
            <p className="text-sm text-muted-foreground/75">
              {debouncedQuery ? `No results for "${debouncedQuery}"` : 'Log entries will appear here as activity occurs.'}
            </p>
            {debouncedQuery && (
              <Button variant="outline" size="sm" onClick={handleClearSearch} className="mt-2">
                Clear search
              </Button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[80px]">Level</TableHead>
                  <TableHead className="w-[170px]">Timestamp</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead className="hidden md:table-cell w-[200px]">Source</TableHead>
                  <TableHead className="hidden lg:table-cell w-[120px]">Path</TableHead>
                  <TableHead className="hidden lg:table-cell w-[60px]">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map(log => (
                  <TableRow
                    key={log.id}
                    className="cursor-pointer transition-colors hover:bg-muted/50"
                    onClick={() => handleRowClick(log)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        {levelIcon(log.level)}
                        <span className={levelClass(log.level)}>{log.level}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs whitespace-nowrap">
                      {formatTimestamp(log.timestamp)}
                    </TableCell>
                    <TableCell className="max-w-[400px] truncate" title={log.message}>
                      {log.message}
                    </TableCell>
                    <TableCell className="hidden md:table-cell font-mono text-xs max-w-[200px] truncate" title={log.source || ''}>
                      {log.source || '—'}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell font-mono text-xs max-w-[120px] truncate" title={log.requestPath || ''}>
                      {log.requestPath || '—'}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell font-mono text-xs">
                      {log.statusCode ?? '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <Label htmlFor="logsPerPage" className="text-sm">Show</Label>
              <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                <SelectTrigger id="logsPerPage" className="w-[80px]">
                  <SelectValue placeholder="25" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm">per page</span>
            </div>

            {totalPages > 1 && (
              <Pagination className="mt-2 sm:mt-0">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (currentPage > 1) handlePageChange(currentPage - 1);
                      }}
                      aria-disabled={currentPage === 1}
                      className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                    />
                  </PaginationItem>

                  {getPageNumbers().map((page, i) =>
                    typeof page === 'string' ? (
                      <PaginationItem key={`ellipsis-${i}`}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    ) : (
                      <PaginationItem key={page}>
                        <PaginationLink
                          href="#"
                          isActive={page === currentPage}
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(page);
                          }}
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  )}

                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (currentPage < totalPages) handlePageChange(currentPage + 1);
                      }}
                      aria-disabled={currentPage === totalPages}
                      className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </div>
        </>
      )}

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedLog && levelIcon(selectedLog.level)}
              <span className={selectedLog ? levelClass(selectedLog.level) : ''}>
                {selectedLog?.level}
              </span>
              <span className="text-muted-foreground font-normal text-sm">
                #{selectedLog?.id}
              </span>
            </DialogTitle>
            <DialogDescription>
              {selectedLog && formatTimestamp(selectedLog.timestamp)}
              {selectedLog?.requestMethod && selectedLog?.requestPath && (
                <span className="ml-2 font-mono">
                  {selectedLog.requestMethod} {selectedLog.requestPath}
                  {selectedLog.statusCode && ` (${selectedLog.statusCode})`}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {selectedLog && (
            <div className="space-y-4 mt-2">
              <div className="rounded-lg bg-muted/50 p-4">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Message</Label>
                <p className="mt-1 text-sm whitespace-pre-wrap break-words">{selectedLog.message}</p>
              </div>

              {selectedLog.source && (
                <div className="rounded-lg bg-muted/50 p-4">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider">Source</Label>
                  <p className="mt-1 text-sm font-mono">{selectedLog.source}</p>
                </div>
              )}

              {selectedLog.exception && (
                <div className="rounded-lg bg-muted/50 p-4">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider">Exception</Label>
                  <pre className="mt-1 text-xs font-mono bg-muted p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-words max-h-[300px] overflow-y-auto">
                    {selectedLog.exception}
                  </pre>
                </div>
              )}

              {selectedLog.stackTrace && !selectedLog.exception?.includes(selectedLog.stackTrace) && (
                <div className="rounded-lg bg-muted/50 p-4">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider">Stack Trace</Label>
                  <pre className="mt-1 text-xs font-mono bg-muted p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-words max-h-[300px] overflow-y-auto">
                    {selectedLog.stackTrace}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LogsTable;
